/**
 * Anniversary painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const anniversaryPaintingIdeasPack: ToilePack = {
    slug: 'anniversary-painting-ideas',
    category: 'craft',
    name: 'Anniversary painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'balloons', label: 'Two balloons', subject: 'a canvas of two hot-air balloons drifting together over green hills at sunrise'},
        {key: 'stars', label: 'Two stars', subject: 'a canvas of a deep blue night sky with a crescent moon and two bright stars side by side'},
        {key: 'roses', label: 'Rose bouquet', subject: "a canvas of a bouquet of red roses with baby's breath, petals painted thick"},
    ],
    styles: [
        {key: 'blended', label: 'Soft blend', style: 'smooth acrylic with softly blended gradients and fine details', palette: 'warm romantic tones of rose, gold and cream', framing: 'the canvas seen straight on, filling most of the frame, a strip of warm wall around it'},
        {key: 'impasto', label: 'Impasto', style: 'textured impasto with thick raised strokes that catch the light', palette: 'deep jewel tones', framing: 'the canvas tied with a satin ribbon as a gift, propped on a table beside a small card envelope'},
        {key: 'dreamy', label: 'Dreamy wash', style: 'loose dreamy acrylic washes with soft edges and a glowing white ground', palette: 'pale pastels', framing: 'the canvas propped on a mantel beside a small vase of flowers and a lit candle'},
    ],
    directions: ['a beach sunset', 'first home', 'for parents'],
    directionHint: 'try: a beach sunset',
    seo: {
        title: 'Anniversary Painting Ideas: Two Balloons, Two Stars, Roses',
        metaDescription: 'Nine anniversary painting ideas, a handmade gift: two hot-air balloons, two bright stars and red roses, in soft, thick and dreamy finishes.',
        h1: 'Anniversary painting ideas: two balloons, two stars, red roses',
        tagline: 'Nine finished canvases, three gift subjects, each in a soft blend, thick impasto or a dreamy wash.',
        intro: "An anniversary painting is a gift made by hand, a picture that says the two of you without words. These nine show three subjects: two hot-air balloons drifting together over green hills at sunrise, a night sky with a crescent moon and two bright stars side by side, and a bouquet of red roses with baby's breath. Type another subject, such as a beach sunset or a first home, and the same gift idea carries it.",
        useCases: [
            'an anniversary gift',
            'a present for a milestone year',
            'a canvas for a shared bedroom',
            'a picture to hang at home',
        ],
        faq: [
            {q: 'How is the canvas presented as a gift?', a: 'The impasto picture is shown tied with a satin ribbon, propped on a table beside a small card envelope.'},
            {q: 'Which subjects show two people?', a: 'The balloons and the stars show two of each: two balloons drifting together, and two stars side by side.'},
            {q: 'What are the three finishes?', a: 'A soft blend with fine details, a thick impasto with raised strokes, and a dreamy wash with a glowing white ground.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the gift setting and the three finishes carry it.'},
        ],
    },
}
